import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

interface ArtworkFormProps {
  artwork: {
    artwork_id: number;
    title: string;
    art_technique: string | null;
    description: string | null;
    creation_date: string | null;
    location: string | null;
    verified: boolean | null;
    creator_id: number;
    preview: string;
    highres_file: number;
  };
  onClose: () => void;
}

const artworkFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  art_technique: z.string().nullable(),
  description: z.string().nullable(),
  creation_date: z.string().nullable(),
  location: z.string().nullable(),
  verified: z.boolean().default(false),
});

type ArtworkFormData = z.infer<typeof artworkFormSchema>;

export function ArtworkForm({ artwork, onClose }: ArtworkFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<ArtworkFormData>({
    resolver: zodResolver(artworkFormSchema),
    defaultValues: {
      title: artwork.title,
      art_technique: artwork.art_technique || "",
      description: artwork.description || "",
      creation_date: artwork.creation_date || "",
      location: artwork.location || "",
      verified: artwork.verified || false,
    },
  });

  const onSubmit = async (data: ArtworkFormData) => {
    try {
      console.log('Current artwork data:', artwork);
      console.log('Form data to update:', data);
      
      const { data: result, error } = await supabase.rpc('update_artwork', {
        p_artwork_id: artwork.artwork_id,
        p_title: data.title,
        p_art_technique: data.art_technique || null,
        p_description: data.description || null,
        p_creation_date: data.creation_date || null,
        p_location: data.location || null,
        p_verified: data.verified
      });

      if (error) {
        console.error('Supabase RPC error:', error);
        throw error;
      }

      await queryClient.invalidateQueries({ queryKey: ["artworks-list"] });
      
      toast({
        title: "Success",
        description: "Artwork details updated successfully",
      });
      
      onClose();
    } catch (error) {
      console.error('Update operation error:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : "An unexpected error occurred";
      
      toast({
        title: "Update Failed",
        description: `Error updating artwork: ${errorMessage}. Please try again.`,
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input placeholder="Enter artwork title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="art_technique"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Technique</FormLabel>
              <FormControl>
                <Input 
                  placeholder="How the artwork was created - short description" 
                  {...field} 
                  value={field.value || ""} 
                />
              </FormControl>
              <FormDescription>
                Describe the technique used to create this artwork
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Enter gallery-style caption for your artwork"
                  className="resize-none" 
                  {...field} 
                  value={field.value || ""} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="creation_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Creation Date</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="e.g., 2023 or March 2023" 
                    {...field} 
                    value={field.value || ""} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Place of origin (country/city)</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="e.g., France/Paris" 
                    {...field} 
                    value={field.value || ""} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="verified"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-2 space-y-0">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="font-normal">Verified Artwork</FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
